/**
 * 分享链召回页（C6-min 验收页）。
 *
 * Phase 4-4 / Stream 4 / Plan §12 / Design §5.8.
 *
 * 功能：
 *  - 强制至少 workId/userId/chapterId 之一查询（防全表扫；service 层也强制）
 *  - 列表 + status 切片
 *  - 单条召回：弹 modal 提示先通知后作废 + 可选 immediate=true (紧急违规)
 *  - 批量召回：多选行 → 顶部"批量召回"按钮 → 循环单次调用（与 C3 不同：
 *    shares API 无 batch endpoint，前端循环；失败者收集错误展示）
 *
 * 权限：
 *  - share:read   (super_admin/operator/support)
 *  - share:revoke (super_admin/operator)
 *
 * "先通知后作废" 警告必显，与 C3 vouchers 风格一致。
 */
import { useMemo, useState } from 'react';
import {
  Card, Table, Form, Input, Button, Space, Tag, Modal, Alert, message,
  Select, Tooltip, Checkbox, Popconfirm,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PermissionGate } from '../../components/PermissionGate';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../contracts/permissions';
import {
  listShares, revokeShare,
  type ShareDto, type ShareStatus, type ListSharesFilter,
} from '../../api/shares';

function formatTs(ms: number | null): string {
  if (ms == null) return '-';
  return new Date(ms).toLocaleString('zh-CN');
}

function statusColor(s: ShareStatus): string {
  switch (s) {
    case 'active': return 'green';
    case 'pending_revoke': return 'orange';
    case 'revoked': return 'red';
  }
}

function statusLabel(s: ShareStatus): string {
  switch (s) {
    case 'active': return '生效中';
    case 'pending_revoke': return '撤销中';
    case 'revoked': return '已撤销';
  }
}

function SharesPageInner() {
  const qc = useQueryClient();
  const canRevoke = usePermission(PERMISSIONS.SHARE_REVOKE);

  const [filterForm] = Form.useForm<{
    workId?: string; userId?: string; chapterId?: string; status?: ShareStatus;
  }>();
  const [filters, setFilters] = useState<ListSharesFilter | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);

  // 单条 revoke modal
  const [revokeTarget, setRevokeTarget] = useState<ShareDto | null>(null);
  const [revokeForm] = Form.useForm<{ reason: string; immediate?: boolean }>();

  // 批量 revoke modal
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchForm] = Form.useForm<{ reason: string; immediate?: boolean }>();
  const [batchResults, setBatchResults] = useState<{
    succeeded: string[]; failed: { shareId: string; message: string }[];
  } | null>(null);
  const [isBatchLoading, setIsBatchLoading] = useState(false);

  const listQuery = useQuery({
    queryKey: ['shares', filters],
    queryFn: () => listShares(filters!),
    enabled: filters !== null,
  });

  const errorHandler = (label: string) =>
    (e: { response?: { data?: { message?: string } }; message?: string }) =>
      message.error(e.response?.data?.message ?? e.message ?? label);

  const singleRevokeMutation = useMutation({
    mutationFn: (vars: { shareId: string; reason: string; immediate?: boolean }) =>
      revokeShare(vars.shareId, { reason: vars.reason, immediate: vars.immediate }),
    onSuccess: (data) => {
      if (data.status === 'revoked') {
        message.success('已立即撤销（用户已收到通知）');
      } else {
        message.success(
          `已 schedule 撤销，预计 ${formatTs(data.revokeScheduledAt ?? null)} 完成（用户已收到通知）`,
        );
      }
      setRevokeTarget(null);
      revokeForm.resetFields();
      qc.invalidateQueries({ queryKey: ['shares'] });
    },
    onError: errorHandler('撤销失败'),
  });

  function applyFilters() {
    filterForm.validateFields().then((v) => {
      const next: ListSharesFilter = {};
      if (v.workId?.trim()) next.workId = v.workId.trim();
      if (v.userId?.trim()) next.userId = v.userId.trim();
      if (v.chapterId?.trim()) next.chapterId = v.chapterId.trim();
      if (v.status) next.status = v.status;
      if (!next.workId && !next.userId && !next.chapterId) {
        message.error('workId / userId / chapterId 至少填一个');
        return;
      }
      setFilters(next);
      setSelectedKeys([]);
    });
  }

  function resetFilters() {
    filterForm.resetFields();
    setFilters(null);
    setSelectedKeys([]);
  }

  function submitSingleRevoke() {
    if (!revokeTarget) return;
    revokeForm.validateFields().then((v) => {
      const immediate = v.immediate === true;
      const doMutate = () => singleRevokeMutation.mutate({
        shareId: revokeTarget.shareId,
        reason: v.reason.trim(),
        immediate,
      });
      // 立即作废不可逆，强制二次确认（不依赖只一个 Checkbox）
      if (immediate) {
        Modal.confirm({
          title: '确认立即作废？',
          content: `分享 ${revokeTarget.shareId} 将立即变为 revoked 状态，无 24h 缓冲。仅紧急违规内容请这样操作。`,
          okText: '确认立即作废', okType: 'danger', cancelText: '取消',
          onOk: doMutate,
        });
      } else {
        doMutate();
      }
    });
  }

  /**
   * 批量召回：前端循环单次调用 (shares 内部 API 无 batch endpoint)。
   * 收集成功 / 失败列表，全部完成后展示报告。
   */
  async function submitBatchRevoke() {
    const v = await batchForm.validateFields().catch(() => null);
    if (!v) return;
    const targets = (listQuery.data?.items ?? []).filter(
      (s) => selectedKeys.includes(s.shareId) && s.status === 'active',
    );
    if (targets.length === 0) {
      message.error('选中行内无 active 状态可召回');
      return;
    }

    setIsBatchLoading(true);
    const succeeded: string[] = [];
    const failed: { shareId: string; message: string }[] = [];
    try {
      for (const s of targets) {
        try {
          await revokeShare(s.shareId, {
            reason: v.reason.trim(),
            immediate: v.immediate === true,
          });
          succeeded.push(s.shareId);
        } catch (e: unknown) {
          const err = e as { response?: { data?: { message?: string } }; message?: string };
          failed.push({
            shareId: s.shareId,
            message: err.response?.data?.message ?? err.message ?? 'unknown',
          });
        }
      }
    } finally {
      setIsBatchLoading(false);
    }
    setBatchResults({ succeeded, failed });
    if (failed.length === 0) {
      message.success(`批量召回完成：${succeeded.length} 个全部成功`);
    } else {
      message.warning(`批量召回部分失败：${succeeded.length} 成 / ${failed.length} 败`);
    }
    setSelectedKeys([]);
    qc.invalidateQueries({ queryKey: ['shares'] });
  }

  const activeSelectedCount = useMemo(
    () => (listQuery.data?.items ?? [])
      .filter((s) => selectedKeys.includes(s.shareId) && s.status === 'active').length,
    [listQuery.data, selectedKeys],
  );

  const columns: ColumnsType<ShareDto> = [
    { title: 'shareId', dataIndex: 'shareId', key: 'sid', width: 180, ellipsis: true },
    { title: 'workId', dataIndex: 'workId', key: 'wid', width: 160, ellipsis: true },
    { title: 'chapterId', dataIndex: 'chapterId', key: 'cid', width: 160, ellipsis: true },
    { title: 'userId (owner)', dataIndex: 'userId', key: 'uid', width: 180, ellipsis: true },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 100,
      render: (s: ShareStatus) => <Tag color={statusColor(s)}>{statusLabel(s)}</Tag>,
    },
    { title: 'token', dataIndex: 'shareToken', key: 'tok', width: 140, ellipsis: true },
    { title: '创建', dataIndex: 'createdAt', key: 'ca', width: 170, render: formatTs },
    {
      title: '撤销 cron @',
      dataIndex: 'revokeScheduledAt', key: 'rs', width: 170,
      render: (v: number | null) => v == null ? '-' : formatTs(v),
    },
    {
      title: '操作', key: 'actions', width: 100,
      render: (_: unknown, row) => (
        row.status === 'active' ? (
          <PermissionGate point={PERMISSIONS.SHARE_REVOKE}>
            <Tooltip title="先通知，默认 24h 后 cron 完成；可勾选立即作废">
              <Button
                size="small" danger
                onClick={() => {
                  setRevokeTarget(row);
                  revokeForm.resetFields();
                }}
                data-testid={`share-revoke-${row.shareId}`}
              >
                召回
              </Button>
            </Tooltip>
          </PermissionGate>
        ) : null
      ),
    },
  ];

  return (
    <Card title="分享链召回（C6-min）" data-testid="shares-page">
      <Alert
        type="info" showIcon
        message="按 workId / userId / chapterId 查询；至少填一个（防全表扫）。召回采用「先通知 24h 后作废」两段式，紧急违规可勾选立即作废。"
        style={{ marginBottom: 16 }}
      />

      <Form
        form={filterForm} layout="inline"
        onFinish={applyFilters}
        data-testid="shares-filter-form"
        style={{ marginBottom: 16, rowGap: 8 }}
      >
        <Form.Item name="workId" label="workId">
          <Input style={{ width: 200 }} placeholder="精确匹配" />
        </Form.Item>
        <Form.Item name="userId" label="userId">
          <Input style={{ width: 200 }} placeholder="精确匹配" />
        </Form.Item>
        <Form.Item name="chapterId" label="chapterId">
          <Input style={{ width: 200 }} placeholder="精确匹配" />
        </Form.Item>
        <Form.Item name="status" label="状态">
          <Select placeholder="任意" allowClear style={{ width: 130 }}
            options={[
              { value: 'active', label: '生效中' },
              { value: 'pending_revoke', label: '撤销中' },
              { value: 'revoked', label: '已撤销' },
            ]}
          />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit">查询</Button>
            <Button onClick={resetFilters}>重置</Button>
          </Space>
        </Form.Item>
      </Form>

      {canRevoke && activeSelectedCount > 0 && (
        <Space style={{ marginBottom: 12 }}>
          <Button
            type="primary" danger
            onClick={() => setBatchOpen(true)}
            data-testid="shares-batch-revoke-btn"
          >
            批量召回（{activeSelectedCount} 个 active）
          </Button>
        </Space>
      )}

      {filters === null ? (
        <Alert type="warning" message="请先查询" style={{ marginTop: 16 }} />
      ) : (
        <Table<ShareDto>
          rowKey="shareId"
          loading={listQuery.isLoading}
          dataSource={listQuery.data?.items ?? []}
          columns={columns}
          pagination={false}
          rowSelection={canRevoke ? {
            selectedRowKeys: selectedKeys,
            onChange: setSelectedKeys,
            getCheckboxProps: (row) => ({ disabled: row.status !== 'active' }),
          } : undefined}
          data-testid="shares-table"
        />
      )}

      {/* 单条 revoke modal */}
      <Modal
        title={`召回 ${revokeTarget?.shareId ?? ''}`}
        open={revokeTarget !== null}
        onCancel={() => { setRevokeTarget(null); revokeForm.resetFields(); }}
        onOk={submitSingleRevoke}
        confirmLoading={singleRevokeMutation.isPending}
        destroyOnClose
        data-testid="shares-revoke-modal"
      >
        <Alert
          type="warning" showIcon
          message="先通知后作废 (两段式 / 与 C3 voucher 同模式)"
          description={
            <span>
              <strong>立即</strong>：触发 share-revoked 订阅消息通知用户<br />
              <strong>24h 后</strong>：content-pipeline 后台 cron flip 到 revoked<br />
              <small>勾选「立即作废」会跳过 24h 缓冲，仅用于紧急违规内容</small>
            </span>
          }
          style={{ marginBottom: 16 }}
        />
        <Form form={revokeForm} layout="vertical" preserve={false}>
          <Form.Item
            label="召回原因（5..200 字符）"
            name="reason"
            rules={[
              { required: true, message: '请填召回原因' },
              { min: 5, max: 200, message: '5..200 字符' },
            ]}
          >
            <Input.TextArea rows={3} maxLength={200} showCount />
          </Form.Item>
          <Form.Item name="immediate" valuePropName="checked">
            <Checkbox data-testid="shares-revoke-immediate">立即作废（仅紧急违规内容）</Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      {/* 批量 revoke modal */}
      <Modal
        title={`批量召回 ${activeSelectedCount} 个分享`}
        open={batchOpen}
        onCancel={() => {
          setBatchOpen(false); batchForm.resetFields(); setBatchResults(null);
        }}
        onOk={submitBatchRevoke}
        confirmLoading={isBatchLoading}
        destroyOnClose
        width={560}
        data-testid="shares-batch-modal"
      >
        <Alert
          type="warning" showIcon
          message="批量召回 — 前端循环单次调用"
          description={
            <span>
              shares API 无批量端点；前端会逐个调用并收集成功/失败列表。<br />
              撤销原因将统一应用于所有选中行；通知逐条触发。
            </span>
          }
          style={{ marginBottom: 16 }}
        />
        <Form form={batchForm} layout="vertical" preserve={false}>
          <Form.Item
            label="召回原因（5..200 字符）"
            name="reason"
            rules={[
              { required: true, message: '请填召回原因' },
              { min: 5, max: 200, message: '5..200 字符' },
            ]}
          >
            <Input.TextArea rows={3} maxLength={200} showCount />
          </Form.Item>
          <Form.Item name="immediate" valuePropName="checked">
            <Checkbox>立即作废（仅紧急违规批量）</Checkbox>
          </Form.Item>
        </Form>
        {batchResults && (
          <Alert
            type={batchResults.failed.length === 0 ? 'success' : 'warning'}
            showIcon style={{ marginTop: 16 }}
            message={`结果：${batchResults.succeeded.length} 成功 / ${batchResults.failed.length} 失败`}
            description={
              batchResults.failed.length > 0 ? (
                <div>
                  失败列表：
                  <ul>
                    {batchResults.failed.map((f) => (
                      <li key={f.shareId}>{f.shareId}: {f.message}</li>
                    ))}
                  </ul>
                </div>
              ) : null
            }
          />
        )}
      </Modal>
    </Card>
  );
}

export default function SharesPage() {
  return (
    <PermissionGate
      point={PERMISSIONS.SHARE_READ}
      fallback={
        <Card>
          <Alert
            type="error" showIcon message="无访问权限"
            description="分享链召回（C6-min）需要 share:read 权限，请联系超管。"
          />
        </Card>
      }
    >
      <SharesPageInner />
    </PermissionGate>
  );
}
